/**
 * Identity prefix used by all agent-side LiveKit / analysis-feed actors.
 *
 * Production server emits actor names like `agent-analyst`, `agent-coach`,
 * etc. Front-end and webhook code use this prefix to distinguish agent
 * messages from human/n8n actors. Keep the constant in `shared/` so server,
 * client, and tests stay aligned.
 */
export const AGENT_IDENTITY_PREFIX = 'agent-';

/**
 * Registered name of the voice agent worker. LiveKit best practice is explicit
 * dispatch: the `/voice/token` route requests this agent via a
 * `RoomAgentDispatch`, and the agent worker registers under the same name
 * (`WorkerOptions.agentName`). The agent/ package is a separate build and
 * duplicates this literal — keep the two in sync.
 */
export const VOICE_AGENT_NAME = 'anaka-voice-agent';

/**
 * Returns true when the supplied identity (LiveKit participant identity or
 * AnalysisFeed actor name) belongs to an agent. Tolerant of nullish input.
 */
export function isAgentParticipant(identity: string | null | undefined): boolean {
  return typeof identity === 'string' && identity.startsWith(AGENT_IDENTITY_PREFIX);
}

/**
 * LiveKit participant-attribute key the voice agent stamps on its own
 * participant to signal an INTENTIONAL end of the session (ANA-575). The value
 * is a {@link VoiceEndReason}.
 *
 * Why an attribute and not a data message: attributes ride the signaling
 * channel (ordered w.r.t. the participant-disconnect event) and the last value
 * is retained on the participant object, so the client can read it
 * synchronously inside its `ParticipantDisconnected` handler — no cross-channel
 * race against the agent's ~2.5–18s-later `room.disconnect()`. Crucially, an
 * old client that does not listen for this key simply ignores it (whereas an
 * unknown *data event* was mis-routed into the widget pipeline), so BOTH
 * co-deploy directions (client-first / agent-first) are safe.
 *
 * The `agent/` package is a separate build and cannot import `shared/` at
 * runtime (its `rootDir` is `./src`), so it duplicates this literal — keep the
 * two in sync (see `agent/src/anaka-agent.ts`).
 */
export const ANAKA_SESSION_END_ATTR = 'anaka.session_end';

/**
 * Why the voice session ended, carried on {@link ANAKA_SESSION_END_ATTR} and
 * through the Cloud voice serializer's `end_reason`.
 *
 * - `complete`      — the model said goodbye (normal end).
 * - `scope_locked`  — a scoping call locked its scope + closed (→ Scoping Locked screen).
 * - `cap`           — a time/turn cap ended the call (resumable — not a deliberate finish).
 * - `guardrail`     — the hardened guardrail terminated the session (hard close → home).
 * - `error`         — an unrecoverable error ended it (NOT treated as a graceful end;
 *                     the client keeps its retryable error surface).
 */
export type VoiceEndReason =
  | 'complete'
  | 'scope_locked'
  | 'cap'
  | 'guardrail'
  | 'error';

/** Canonical ordered list of {@link VoiceEndReason} values. */
export const VOICE_END_REASONS: readonly VoiceEndReason[] = [
  'complete',
  'scope_locked',
  'cap',
  'guardrail',
  'error',
];

/**
 * Validate an arbitrary attribute value into a {@link VoiceEndReason}, or
 * `null` when it is missing/unknown. Never throws — untrusted wire input.
 */
export function parseVoiceEndReason(
  value: string | null | undefined,
): VoiceEndReason | null {
  return typeof value === 'string' && (VOICE_END_REASONS as readonly string[]).includes(value)
    ? (value as VoiceEndReason)
    : null;
}
